/**
 * main.ts — standalone "Notify when available" widget.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. The bundle is intentionally
 * independent of React/the SPA so one HTML snippet can mount it on every
 * upcoming-service page. Only name/email are editable; service context comes
 * from the host page and no email is sent in this phase.
 */
import './widget.css';
import widgetCss from './widget.css?inline';
import { submitNotification } from './api';
import { notifyTemplate } from './template';

const SELECTOR = '[data-gps-notify-widget]';

function setError(form: HTMLFormElement, field: 'name' | 'email', message: string): void {
  const output = form.querySelector<HTMLElement>(`[data-error-for="${field}"]`);
  const input = form.elements.namedItem(field);
  if (output) output.textContent = message;
  if (input instanceof HTMLInputElement) input.setAttribute('aria-invalid', message ? 'true' : 'false');
}

function validate(form: HTMLFormElement): { name: string; email: string } | null {
  const data = new FormData(form);
  const name = String(data.get('name') ?? '').trim();
  const email = String(data.get('email') ?? '').trim().toLowerCase();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  setError(form, 'name', name ? '' : 'Please enter your name.');
  setError(form, 'email', emailValid ? '' : 'Please enter a valid email address.');
  return name && emailValid ? { name, email } : null;
}

/** Mount one instance; repeated calls are safe. */
export function mount(container: HTMLElement): void {
  if (container.dataset.gpsNotifyMounted === 'true') return;
  container.dataset.gpsNotifyMounted = 'true';
  const instanceId = `gps-notify-${Math.random().toString(36).slice(2, 10)}`;
  const root = container.shadowRoot || container.attachShadow({ mode: 'open' });
  root.innerHTML = `<style>${widgetCss}</style>${notifyTemplate(instanceId)}`;
  const form = root.querySelector<HTMLFormElement>('form');
  if (!form) return;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const contact = validate(form);
    if (!contact) return;
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    const message = form.querySelector<HTMLElement>('.message');
    if (!button || !message || button.disabled) return;
    button.disabled = true;
    const buttonLabel = button.querySelector('span');
    if (buttonLabel) buttonLabel.textContent = 'Submitting…';
    message.textContent = '';
    message.className = 'message';

    try {
      await submitNotification(container, contact);
      form.hidden = true;
      const success = document.createElement('div');
      success.className = 'confirmation';
      success.setAttribute('role', 'status');
      success.innerHTML = `
        <h3 class="confirmation__title">Thank you</h3>
        <p class="confirmation__body">We’ve recorded your interest in this service.</p>`;
      form.insertAdjacentElement('afterend', success);
    } catch {
      message.classList.add('message--error');
      message.textContent = 'We couldn’t save your request. Please try again.';
      button.disabled = false;
      if (buttonLabel) buttonLabel.textContent = 'Notify me';
    }
  });
}

/** Mount every configured placeholder on the page. */
export function mountAll(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>(SELECTOR).forEach(mount);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
} else {
  mountAll();
}
